import React from 'react';
import _ from 'lodash';
import { Button, FormControl, Stack, Typography } from '@mui/material';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import DatePicker from '@mui/lab/DatePicker';
import { pl } from 'date-fns/locale';
import { RequiredField } from '../others/RequiredField';
import { useDynamicFunction, useDynamicAction, useDynamicQuery, useFullScreen, useDidMountDebounceEffect } from '../../services/hooks/';
import { formatDate, constructDependsOnParams, handleShowOn } from '../../services/functions';
import { InfoField } from './InfoField';
import { CssTextField } from '../others/CssTextField';
import { useClickOutside } from '../../services/hooks';

export const DateField = props => {
  const { field, formik, getFieldCallback, updateOptionsCallback, setLoadingCallback, setDateToCallback, formVersion, params, boskieWakacje } = props;
  const action = useDynamicAction(
    field.depends_on,
    field.on_change_splitted ? field.on_change_splitted[1] : field.on_change,
    constructDependsOnParams(field.depends_on, field.name, formik.values, params, null, boskieWakacje
    ),
    formik.values[field.name]
  );
  const isMobile = useFullScreen();
  const [ghostValue, setGhostValue] = React.useState('');
  const { query, data, loading } = useDynamicQuery(action);
  const dynamicFunction = useDynamicFunction(field.on_change_splitted ? field.on_change_splitted[0] : field.on_change);
  const wrapperRef = React.useRef(null);
  const clickOutside = useClickOutside(wrapperRef);
  const [open, setOpen] = React.useState(false);
  const [today, setToday] = React.useState(new Date());
  const [error, setError] = React.useState(false);
  const [tempDate, setTempDate] = React.useState(formik.values[field.name] || '');
  const formikError = formik.touched[field.name] && Boolean(formik.errors[field.name]);


  React.useEffect(() => {
    if (data.message) {
      alert(data.message);
    }
    updateOptionsCallback(data);
    if (data.url_params_data) {
      formik.setFieldValue('event', data.event[0].id);
      if (!data.only_event) {
        formik.setFieldValue('camp', data.camp[0].id);
      }
      formik.setFieldValue('resort', data.resort[0].id);
    }
  }, [data]);

  React.useEffect(() => {
    setLoadingCallback(loading);
  }, [loading]);

  useDidMountDebounceEffect(() => {
    if (!isMobile) {
      handleChange(tempDate);
    }
  }, [tempDate]);

  const handleChange = val => {
    formik.setFieldTouched(field.name, true);
    if (isNaN(val.valueOf())) {
      setError(true);

      return;
    }
    setError(false);
    if (!_.isNil(dynamicFunction)) {
      if (dynamicFunction.functionName === 'setEmptyChoicesFunction') {
        const newObjects = {};
        _.forEach(dynamicFunction.data, item => {
          newObjects[item] = '';
        });
        newObjects[field.name] = formatDate(val);
        formik.setValues({ ...formik.values, ...newObjects });
        // _.forEach(dynamicFunction.data, item => formik.setFieldValue(item, ''));
      }
    } else if (field.name == 'period_from') {
      setDateToCallback(val);
    } else {
      formik.setFieldValue(field.name, formatDate(val));
    }
    if (isMobile) {
      setOpen(false);
    }
  };

  const shouldDisableDateHandler = date => {
    if (field.name == 'period_from') {
      if ((/^202[456789]/).test(formVersion.toString())) {
        // To co roku trzeba zmieniać
        // if (![2024, 2025].includes(date.getFullYear())) {
        //   return true;
        // }
        if (parseInt(formVersion.toString().slice(0, 4)) !== date.getFullYear()) {
          return true;
        }
        if (![3, 4, 5, 8].includes(date.getMonth())) {
          return true;
        }

      } else {
        if (date.getFullYear() !== 2023) {
          return true;
        }
        if (![3, 4, 5, 8].includes(date.getMonth())) {
          return true;
        }
      }

    }

    return false;
  };

  const handleClose = () => {
    if (clickOutside) {
      setOpen(false);
    }
  };
  const handleOpen = () => {
    setOpen(true);
  };

  function CustomToolbar(props) {
    const { date } = props;

    return (
      <div style={{ padding: '16px' }}>
        <div style={{ fontSize: '14px', opacity: 0.7, marginBottom: 8 }}>
          Wybierz datę
        </div>

        <Typography color="primary" variant="button">
          {formatDate(date)}
        </Typography>
      </div>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} locale={pl} >
      <FormControl style={{ display: handleShowOn(
        field.show_on, formik.values,
        getFieldCallback(!_.isNil(field.show_on) ? field.show_on.trim().split(' ')[0].split('-')[0] : []))
        ? ''
        : 'none', paddingBottom: 15 }} fullWidth>
        <Typography variant="subtitle1" style={{ paddingBottom: 15 }}>
          <b>{field.label}</b>
          <RequiredField required={field.required} isEmpty={_.isEmpty(formik.values[field.name])} formikError={formikError} />
        </Typography>
        { 'resort' in params
          ? <DatePicker
            disableOpenPicker={true}
            showToolbar={false}
            disablePast={field.name == 'period_from' ? true : false}
            shouldDisableYear={date => field.name == 'period_from' ? date.getFullYear() > today.getFullYear() + 1 ? true : false : false}
            shouldDisableDate={date => shouldDisableDateHandler(date)}
            disabled={field.disabled}
            inputFormat="dd.MM.yyyy"
            onClose={handleClose}
            mask="__.__.____"
            open={open}
            label={_.isEmpty(formik.values[field.name]) ? field.placeholder : null}
            value={formik.values[field.name]}
            onAccept={val => handleChange(val)}
            onChange={val => setGhostValue(val)}
            ToolbarComponent={CustomToolbar}
            okText="Zatwierdź"
            cancelText="Anuluj"
            defaultCalendarMonth={
              formVersion.toString().startsWith('2024')
                ? new Date(2024, 3, 1, 0, 0, 0, 0)
                : formVersion.toString().startsWith('2025')
                  ? new Date(2025, 3, 1, 0, 0, 0, 0)
                  : today}
            renderInput={props => <Stack>
              <CssTextField
                // onKeyDown={e => 'resort' in params ? e.preventDefault() : null}
                {...props}
                onClick={handleOpen}
                variant="outlined"
                error={error}
                helperText={error && 'Proszę podać odpowiednią datę'}
                name={field.name}

              />

              { isMobile ? null
                : <Button size="large" onClick={() => handleChange(ghostValue)} variant="contained">
                  Zatwierdź datę
                </Button>
              }
            </Stack>
            }

            KeyboardButtonProps={{
              'aria-label': 'change date'
            }}
          />
          : <DatePicker
            disablePast={field.name == 'period_from' ? true : false}
            shouldDisableYear={date => field.name == 'period_from' ? date.getFullYear() > today.getFullYear() + 1 ? true : false : false}
            shouldDisableDate={date => shouldDisableDateHandler(date)}
            disabled={field.disabled}
            inputFormat="dd.MM.yyyy"
            onClose={handleClose}
            mask="__.__.____"
            open={open}
            label={_.isEmpty(formik.values[field.name]) ? field.placeholder : null}
            value={formik.values[field.name]}
            onChange={newValue => {
              setTempDate(newValue); // tylko lokalnie
            }}
            onAccept={finalValue => {
              handleChange(finalValue); // dopiero tu zapis do formika
            }}
            ToolbarComponent={CustomToolbar}
            okText="Zatwierdź"
            cancelText="Anuluj"
            defaultCalendarMonth={formVersion.toString().startsWith('2024')
              ? new Date(2024, 3, 1, 0, 0, 0, 0)
              : formVersion.toString().startsWith('2025')
                ? new Date(2025, 3, 1, 0, 0, 0, 0)
                : today}
            renderInput={props => <CssTextField
              {...props}
              onClick={handleOpen}
              variant="outlined"
              error={error || formikError}
              helperText={
                error
                  ? 'Proszę podać odpowiednią datę'
                  : formik.touched[field.name] && formik.errors[field.name]
              }
              name={field.name}

            />}
            KeyboardButtonProps={{
              'aria-label': 'change date'
            }}
          />

        }
        {
          !_.isEmpty(field.infos)
            ? _.map(field.infos, (info, key) => <InfoField info={info} key={key} getFieldCallback={getFieldCallback} formikValues={formik.values} />)
            : null
        }
      </FormControl>
    </LocalizationProvider>
  );
};
